import clsx from 'clsx'
import Image from 'next/image'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import type {
  KinerjaPraktikPembelajaranDimensi,
  KinerjaProfil,
} from 'types/kinerja'

interface CustomListProps extends React.ComponentProps<'ul'> {
  lists: {
    label: string
    description: string
    value: string
    color: string
  }[]
}

function CustomList({ lists, ...props }: CustomListProps) {
  return (
    <ul className="border border-gray-20 rounded-[4px]" {...props}>
      {lists.map((list, index) => (
        <li
          key={index}
          className="p-3 flex items-center border-gray-20 border-b last:border-b-0"
        >
          <div className="flex flex-col gap-1 flex-1">
            <Text variant="action">{list.label}</Text>
            <Text variant="body-sm" color="subdued">
              {list.description}
            </Text>
          </div>

          <div>
            <div className={clsx('px-6 py-2 rounded-[34px]', list.color)}>
              <Text color="inverse" variant="body-bold">
                {list.value}
              </Text>
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

interface DialogDimensiDetailProps extends Omit<DialogProps, 'children'> {
  contentPraktikPembelajaran: KinerjaPraktikPembelajaranDimensi[]
  profile: KinerjaProfil
}

export default function DialogDimensiDetail({
  contentPraktikPembelajaran,
  profile,
  ...props
}: DialogDimensiDetailProps) {
  const getStatus = (dimensi: KinerjaPraktikPembelajaranDimensi) => {
    if (dimensi.skor === 1) {
      return {
        value: 'Kurang',
        color: 'bg-red-50',
      }
    }

    if (dimensi.skor === 2) {
      return {
        value: 'Cukup',
        color: 'bg-orange-40',
      }
    }

    return {
      value: 'Baik',
      color: 'bg-green-50',
    }
  }

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent>
        <DialogHeader className="flex gap-2 items-center">
          <div className="flex flex-1 flex-col gap-2">
            <Text variant="subheading" color="subdued" className="uppercase">
              Rapor Pendidikan SATUAN PENDIDIKAN Anda
            </Text>
            <Text variant="heading-md">{profile.satuanPendidikan}</Text>
          </div>
          <Button
            size="md"
            color="white"
            as="a"
            href="https://raporpendidikan.kemdikbud.go.id/download-report"
            target="_blank"
            rel="noreferrer"
          >
            <span className="flex items-center">
              Ke sistem Rapor Pendidikan
              <Icon>keyboard_arrow_right</Icon>
            </span>
          </Button>
        </DialogHeader>
        <div className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-4 max-w-[680px]">
            <div className="px-4 py-2 flex gap-4 items-center bg-[#FAFAFA] rounded-[4px]">
              <div className="w-16 h-16 relative">
                {/* Prevent user select */}
                <div className="absolute inset-0 z-10">&nbsp;</div>

                <Image
                  alt=""
                  src="/illustrations/indicator-illustration.svg"
                  width={64}
                  height={64}
                />
              </div>
              <Text variant="action">Kualitas Pembelajaran</Text>
              <div className="border-r border-gray-20 h-16">&nbsp;</div>
              <div className="flex flex-col">
                <Text color="subdued">
                  Kualitas pengelolaan kelas dan penyelenggaraan pembelajaran
                  interaktif yang sesuai dengan tujuan pembelajaran dan
                  karakteristik siswa.
                </Text>
              </div>
            </div>

            <Text variant="heading-sm">Dimensi</Text>

            <CustomList
              lists={[
                {
                  label: 'D.1.1 Manajemen Kelas',
                  description: `Pengelolaan kelas yang mendukung pembelajaran serta penerapan penghargaan dan sanksi secara proporsional.`,
                  ...getStatus(
                    contentPraktikPembelajaran.find(
                      (dimensi) => dimensi.dimensiNama === 'Manajemen Kelas'
                    )
                  ),
                },
                {
                  label: 'D.1.2 Dukungan Psikologis',
                  description: `Praktik pembelajaran yang memenuhi kebutuhan psikologis siswa untuk menumbuhkan kepercayaan diri dan perasaan diterima tanpa dibeda-bedakan.`,
                  ...getStatus(
                    contentPraktikPembelajaran.find(
                      (dimensi) => dimensi.dimensiNama === 'Dukungan Psikologis'
                    )
                  ),
                },
                {
                  label: 'D.1.3 Metode Pembelajaran',
                  description: `Praktik pembelajaran interaktif yang sesuai dengan tujuan pembelajaran dan karakteristik siswa.`,
                  ...getStatus(
                    contentPraktikPembelajaran.find(
                      (dimensi) => dimensi.dimensiNama === 'Metode Pembelajaran'
                    )
                  ),
                },
              ]}
            />

            <Text className="p-4 rounded-[4px] bg-[color:var(--color-surface-informational-subdued)]">
              <strong>Catatan</strong>: Anda dapat berdiskusi dengan atasan
              untuk mempelajari Rapor Pendidikan Satdik Anda atau pun info
              lainnya tentang tahap Perencanaan.
            </Text>
          </div>
          <div className="flex justify-center">
            <Button size="lg" onClick={() => props.onClose?.()}>
              Saya mengerti
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
